/**
 * Platform-level feature gates. Edited from MAIE.Console (`/flags`); the
 * resolver in MAIE.Backend persists `platform_flags` + per-tenant
 * overrides and serves the merged map at `/me/platform-flags`.
 *
 * Boot flow:
 *   - At app start, components see `STATIC_FALLBACK` (everything off).
 *   - Once the user authenticates and the auth store calls
 *     `usePlatformFlagsStore.refresh()`, real values take over.
 *   - Per-tenant overrides come merged in for the active tenant — no
 *     extra wiring needed in components.
 *
 * Components should prefer `usePlatformFlag('FLAG_KEY')`. The
 * `PLATFORM_FLAGS` named export is preserved for components that read it
 * outside React (one-shot module-level conditions); reads from there are
 * always the latest snapshot but won't trigger re-renders, so use the
 * hook in component bodies.
 */
import { create } from 'zustand';
import {
  FlagKey,
  FlagMap,
  platformFlagsApi,
} from '@/services/platformFlagsApi';

const STATIC_FALLBACK: Record<FlagKey, boolean> = {
  BOTS_AND_MESSAGING_ENABLED: false,
  PAID_PLANS_ENABLED: false,
  // Per-channel kill switches default to ON — when bots are gated by
  // BOTS_AND_MESSAGING_ENABLED, individual channels are presumed
  // available unless platform admin explicitly disables one.
  WHATSAPP_BOT_ENABLED: true,
  TELEGRAM_BOT_ENABLED: true,
  // Pre-auth fallback off. After login, the real value (default ON in
  // the backend registry) flows in via /me/platform-flags.
  AI_SCHEDULE_GENERATOR_ENABLED: false,
  AI_OCR_ENABLED: false,
  AI_OCR_RX_ENABLED: false,
  AI_OCR_ATTACHMENTS_ENABLED: false,
  AI_OCR_LAB_ENABLED: false,
  AI_OCR_IMAGING_ENABLED: false,
  AI_OCR_INBOX_ENABLED: false,
  AI_OCR_RECORD_ENABLED: false,
  AI_TIMELINE_ENABLED: false,
  SIMPLE_CHART_ENABLED: false,
  // Pre-auth fallback off = the pre-diet sidebar. A tenant opts IN.
  NAV_DIET_ENABLED: false,
  DOCTOR_QUEUE_HOME_ENABLED: false,
  AI_DEEP_RESEARCH_ENABLED: false,
  // Pre-auth fallback off; real values flow in via /me/platform-flags.
  PATIENT_PORTAL_ENABLED: false,
  PATIENT_PORTAL_ENCOUNTERS_VISIBLE: false,
  // Extra STT load per consult — off until enabled per-tenant.
  SCRIBE_FAST_PARTIALS: false,
};

interface PlatformFlagsState {
  flags: FlagMap;
  loaded: boolean;
  refresh: () => Promise<void>;
}

export const usePlatformFlagsStore = create<PlatformFlagsState>((set) => ({
  flags: { ...STATIC_FALLBACK },
  loaded: false,
  refresh: async () => {
    try {
      const fresh = await platformFlagsApi.getMine();
      set({ flags: { ...STATIC_FALLBACK, ...fresh }, loaded: true });
    } catch {
      // Network or unauth errors leave the static fallback in place —
      // safer than letting a transient blip flip "coming-soon" surfaces
      // open. The auth store retries refresh after each login.
      set({ loaded: true });
    }
  },
}));

/**
 * React hook — re-renders the component when the flag flips.
 */
export function usePlatformFlag(key: FlagKey): boolean {
  return usePlatformFlagsStore((s) => s.flags[key] ?? STATIC_FALLBACK[key]);
}

/**
 * Snapshot accessor for non-React contexts (module-level branches, JSX
 * outside a component body). Reads the current value but doesn't
 * subscribe — call sites in component render paths should use the hook.
 *
 * Shape mimics the legacy const so existing call sites keep working
 * while we incrementally migrate to the hook.
 */
export const PLATFORM_FLAGS: Record<FlagKey, boolean> = new Proxy(
  STATIC_FALLBACK,
  {
    get(_target, prop: string) {
      const live = usePlatformFlagsStore.getState().flags;
      if (prop in live) return live[prop];
      return STATIC_FALLBACK[prop as FlagKey] ?? false;
    },
  },
) as Record<FlagKey, boolean>;
